import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Form, Input, Select, DatePicker, message, Row, Col, Statistic, InputNumber } from 'antd';
import { SearchOutlined, PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { get, post, getMerchantApps, getCurrentAppId } from '../../utils/merchantApi';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const MerchantWithdraw = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, total_amount: 0, page: 1, limit: 10 });
  const [filters, setFilters] = useState({});
  const [detailVisible, setDetailVisible] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [methods, setMethods] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();

  useEffect(() => {
    loadData();
    loadMethods();
  }, [pagination.page]);

  const loadMethods = async () => {
    try {
      const response = await get('/merchant/withdraw/methods');
      const data = response.data || {};
      setMethods(data.methods || []);
      setCurrencies(data.currencies || []);
    } catch (error) {
      console.error('获取代付方式失败:', error);
    }
  };

  const loadData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await get('/merchant/withdraw', {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        ...params
      });

      setDataSource(response.data?.list || []);
      setPagination({
        ...pagination,
        total: response.data?.pagination?.total || 0,
        total_amount: response.data?.pagination?.total_amount || 0,
        page: response.data?.pagination?.page || 1
      });
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    delete newFilters.dateRange;
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 });
    loadData({ ...newFilters, page: 1 });
  };

  const handleReset = () => {
    setFilters({});
    setPagination({ ...pagination, page: 1 });
    loadData({ page: 1 });
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await get(`/merchant/withdraw/${record.id}`);
      setDetailData(response.data);
      setDetailVisible(true);
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleMethodChange = (methodCode) => {
    const method = methods.find(m => m.method_code === methodCode);
    setSelectedMethod(method);
    createForm.setFieldsValue({ withdraw_info: {} });
  };

  const handleCurrencyChange = (currency) => {
    const method = createForm.getFieldValue('withdraw_method');
    if (method) {
      const methodInfo = methods.find(m => m.method_code === method);
      if (methodInfo) {
        createForm.setFieldsValue({
          amount: undefined,
          min_amount: methodInfo.min_amount,
          max_amount: methodInfo.max_amount
        });
      }
    }
  };

  const handleCreate = async (values) => {
    setSubmitting(true);
    try {
      const currentAppId = getCurrentAppId();
      if (!currentAppId) {
        message.error('请先选择应用');
        return;
      }

      const methodInfo = methods.find(m => m.method_code === values.withdraw_method);
      
      const withdrawInfo = {};
      if (selectedMethod && selectedMethod.required_fields) {
        selectedMethod.required_fields.forEach(field => {
          withdrawInfo[field.identifier] = values[field.identifier];
        });
        if (selectedMethod.optional_fields) {
          selectedMethod.optional_fields.forEach(field => {
            if (values[field.identifier]) {
              withdrawInfo[field.identifier] = values[field.identifier];
            }
          });
        }
      }

      await post('/merchant/withdraw', {
        amount: values.amount,
        currency_code: values.currency_code,
        withdraw_method: values.withdraw_method,
        withdraw_info: withdrawInfo,
        callback_url: values.callback_url,
        remark: values.remark
      });

      message.success('代付申请提交成功');
      setCreateVisible(false);
      createForm.resetFields();
      setSelectedMethod(null);
      loadData();
    } catch (error) {
      message.error(error.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusTag = (status, auditStatus) => {
    if (auditStatus === 'approved') {
      return <Tag color="green">已通过</Tag>;
    }
    if (auditStatus === 'rejected') {
      return <Tag color="red">已拒绝</Tag>;
    }
    const statusMap = {
      pending: { color: 'orange', text: '待审核' },
      processing: { color: 'blue', text: '处理中' },
      paid: { color: 'green', text: '已完成' },
      failed: { color: 'red', text: '失败' }
    };
    return <Tag color={statusMap[status]?.color || 'default'}>{statusMap[status]?.text || status}</Tag>;
  };

  const renderWithdrawInfoFields = () => {
    if (!selectedMethod || !selectedMethod.required_fields) {
      return null;
    }

    const requiredFields = selectedMethod.required_fields;
    const optionalFields = selectedMethod.optional_fields || [];
    const allFields = [...requiredFields, ...optionalFields];

    return allFields.map((field, index) => (
      <Form.Item
        key={index}
        name={field.identifier}
        label={field.label}
        rules={requiredFields.some(f => f.identifier === field.identifier) ? [{ required: true, message: `请输入${field.label}` }] : []}
      >
        <Input placeholder={`请输入${field.label}`} />
      </Form.Item>
    ));
  };

  const columns = [
    {
      title: '代付单号',
      dataIndex: 'withdraw_no',
      key: 'withdraw_no',
      width: 180
    },
    {
      title: '代付金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (val, record) => `${record.currency_code || 'CNY'} ${Number(val || 0).toFixed(2)}`
    },
    {
      title: '手续费',
      dataIndex: 'fee_amount',
      key: 'fee_amount',
      width: 100,
      render: (val, record) => val ? `${record.currency_code || 'CNY'} ${Number(val).toFixed(2)}` : '-'
    },
    {
      title: '实际到账',
      dataIndex: 'actual_amount',
      key: 'actual_amount',
      width: 120,
      render: (val, record) => val ? `${record.currency_code || 'CNY'} ${Number(val).toFixed(2)}` : '-'
    },
    // {
    //   title: '人民币金额',
    //   dataIndex: 'cny_amount',
    //   key: 'cny_amount',
    //   width: 120,
    //   render: (val, record) => val ? `¥${Number(val).toFixed(2)}` : '-'
    // },
    {
      title: '代付方式',
      dataIndex: 'withdraw_method',
      key: 'withdraw_method',
      width: 100
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => getStatusTag(status, record.audit_status)
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (val) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
          详情
        </Button>
      )
    }
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateVisible(true)}>
            提交代付
          </Button>
        </Space>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline" onFinish={handleSearch}>
          <Form.Item name="status" label="状态">
            <Select placeholder="请选择" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="processing">处理中</Option>
              <Option value="paid">已完成</Option>
              <Option value="failed">失败</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="audit_status" label="审核状态">
            <Select placeholder="请选择" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="日期">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={handleReset}>重置</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Statistic title="代付总笔数" value={pagination.total} />
          </Col>
          <Col>
            <Statistic title="代付总金额" value={Number(pagination.total_amount || 0)} precision={2} suffix="" />
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, page, limit: pageSize });
            }
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="提交代付"
        open={createVisible}
        onCancel={() => {
          setCreateVisible(false);
          createForm.resetFields();
          setSelectedMethod(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="currency_code" label="货币类型" initialValue="CNY" rules={[{ required: true, message: '请选择货币' }]}>
            <Select placeholder="请选择货币" onChange={handleCurrencyChange}>
              {currencies.map(c => (
                <Option key={c.currency_code} value={c.currency_code}>
                  {c.currency_name} ({c.currency_code}) {c.currency_symbol}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="withdraw_method" label="代付方式" rules={[{ required: true, message: '请选择代付方式' }]}>
            <Select placeholder="请选择代付方式" onChange={handleMethodChange}>
              {methods.map(m => (
                <Option key={m.method_code} value={m.method_code}>
                  {m.method_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="代付金额"
            rules={[
              { required: true, message: '请输入代付金额' },
              {
                type: 'number',
                min: selectedMethod?.min_amount || 0,
                max: selectedMethod?.max_amount || Infinity,
                message: selectedMethod?.min_amount || selectedMethod?.max_amount 
                  ? `代付金额范围: ${selectedMethod.min_amount || 0} - ${selectedMethod.max_amount || '无限制'}`
                  : undefined
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={`请输入代付金额`}
              min={0}
              precision={2}
            />
          </Form.Item>
          {selectedMethod && (selectedMethod.min_amount > 0 || selectedMethod.max_amount > 0) && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col>
                <span style={{ color: '#999' }}>
                  限额: {selectedMethod.min_amount || 0} - {selectedMethod.max_amount || '无限制'} {createForm.getFieldValue('currency_code') || 'CNY'}
                </span>
              </Col>
            </Row>
          )}
          {renderWithdrawInfoFields()}
          <Form.Item name="callback_url" label="回调地址">
            <Input placeholder="代付状态变更通知地址（可选）" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注（可选）" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>提交申请</Button>
              <Button onClick={() => {
                setCreateVisible(false);
                createForm.resetFields();
                setSelectedMethod(null);
              }}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="代付详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>关闭</Button>
        ]}
        width={600}
      >
        {detailData && (
          <Row gutter={[16, 12]}>
            <Col span={12}><strong>代付单号：</strong>{detailData.withdraw_no}</Col>
            <Col span={12}><strong>代付金额：</strong>{detailData.currency_code} {Number(detailData.amount).toFixed(2)}</Col>
            <Col span={12}><strong>汇率：</strong>{detailData.exchange_rate || 1}</Col>
            <Col span={12}><strong>人民币金额：</strong>¥{Number(detailData.cny_amount || 0).toFixed(2)}</Col>
            <Col span={12}><strong>手续费：</strong>{detailData.currency_code} {Number(detailData.fee_amount || 0).toFixed(2)}</Col>
            <Col span={12}><strong>实际到账：</strong>{detailData.currency_code} {Number(detailData.actual_amount || 0).toFixed(2)}</Col>
            <Col span={12}><strong>代付方式：</strong>{detailData.withdraw_method}</Col>
            <Col span={12}><strong>状态：</strong>{getStatusTag(detailData.status, detailData.audit_status)}</Col>
            <Col span={12}><strong>审核状态：</strong>{detailData.audit_status === 'approved' ? '已通过' : detailData.audit_status === 'rejected' ? '已拒绝' : '待审核'}</Col>
            <Col span={12}><strong>审核备注：</strong>{detailData.audit_remark || '-'}</Col>
            <Col span={24}>
              <strong>代付信息：</strong>
              {(() => {
                const info = typeof detailData.withdraw_info === 'string' ? JSON.parse(detailData.withdraw_info) : detailData.withdraw_info;
                if (!info || Object.keys(info).length === 0) return '-';
                return (
                  <div style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, marginTop: 8 }}>
                    <Row gutter={[16, 4]}>
                      {Object.entries(info).map(([key, value]) => (
                        <Col span={12} key={key}>
                          <span style={{ color: '#666' }}>{key}: </span>
                          <span>{value}</span>
                        </Col>
                      ))}
                    </Row>
                  </div>
                );
              })()}
            </Col>
            <Col span={12}><strong>创建时间：</strong>{detailData.created_at ? dayjs(detailData.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Col>
            <Col span={12}><strong>审核时间：</strong>{detailData.audit_time ? dayjs(detailData.audit_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Col>
          </Row>
        )}
      </Modal>
    </div>
  );
};

export default MerchantWithdraw;
